import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { BarChart3, FileText, Star, Briefcase, LogOut, Plus, Trash2, Edit3, Save, X, Check, Mail, Inbox, Users, Building2, CheckCircle2, XCircle, Layers, Brain, Eye, EyeOff } from 'lucide-react';

const ACCENT = '#5E0ED7';
const ease = [0.22, 1, 0.36, 1];
const NAV = [
  { id: 'enquiries',    label: 'Enquiries',      icon: Inbox      },
  { id: 'blog',         label: 'Blog Posts',     icon: FileText   },
  { id: 'testimonials', label: 'Testimonials',   icon: Star       },
  { id: 'casestudies',  label: 'Case Studies',   icon: Briefcase  },
  { id: 'clients',      label: 'Clients',        icon: Building2  },
  { id: 'stats',        label: 'Hero Stats',     icon: BarChart3  },
  { id: 'hero',         label: 'Hero Content',   icon: FileText   },
  { id: 'services',     label: 'Services',       icon: Layers     },
  { id: 'methodology',  label: 'Our Process',    icon: Briefcase  },
  { id: 'ai',           label: 'AI Capabilities',icon: Brain      },
  { id: 'industries',   label: 'Industries',     icon: Building2  },
  { id: 'about',        label: 'About Stats',    icon: BarChart3  },
  { id: 'aboutvalues',  label: 'About Values',   icon: Star       },
  { id: 'contact',      label: 'Contact Info',   icon: Mail       },
  { id: 'admins',       label: 'Admin Users',    icon: Users      },
];

const inputCls   = "w-full px-3 py-2.5 rounded-lg text-sm text-gray-900 outline-none border border-gray-200 focus:border-[#5E0ED7] transition-colors bg-white";
const textareaCls= `${inputCls} resize-none`;

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold tracking-[0.1em] uppercase mb-1.5" style={{ color: '#71717A' }}>{label}</label>
      {children}
    </div>
  );
}

// ── Stats ─────────────────────────────────────────────
function StatsPanel() {
  const [stats, setStats]   = useState({ brands: 300, products: 200, ventures: 100 });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);

  useEffect(() => { api.getStats().then(setStats).catch(() => {}); }, []);

  async function save() {
    setSaving(true);
    try { await api.updateStats(stats); setSaved(true); setTimeout(() => setSaved(false), 2000); }
    finally { setSaving(false); }
  }

  return (
    <div>
      <h2 className="text-xl font-bold mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Hero Stats</h2>
      <p className="text-sm text-gray-500 mb-8">Numbers displayed in the hero section of the website.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
        {[['brands','Crafted Brands'],['products','Digital Products'],['ventures','Ventures Funded']].map(([k,l]) => (
          <Field key={k} label={l}>
            <input type="number" className={inputCls} value={stats[k] ?? ''} onChange={e => setStats(p => ({ ...p, [k]: Number(e.target.value) }))} />
          </Field>
        ))}
      </div>
      <button onClick={save} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white" style={{ background: ACCENT }}>
        {saved ? <><Check size={14} /> Saved!</> : saving ? 'Saving...' : <><Save size={14} /> Save Stats</>}
      </button>
    </div>
  );
}

// ── Blog ──────────────────────────────────────────────
const CATEGORIES = ['AI Strategy', 'Security', 'Engineering', 'Product Design', 'Digital Transformation'];

function BlogPanel() {
  const [posts, setPosts]     = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState('');

  const load = () => api.adminGetAllPosts().then(setPosts).catch(() => setPosts([]));
  useEffect(() => { load(); }, []);

  async function save() {
    setSaving(true); setErr('');
    try {
      if (editing.id) await api.updatePost(editing.id, editing);
      else await api.createPost(editing);
      await load(); setEditing(null);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  }

  async function del(id) {
    if (!window.confirm('Delete this post?')) return;
    await api.deletePost(id); await load();
  }

  if (editing !== null) return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{editing.id ? 'Edit' : 'New'} Post</h2>
        <button onClick={() => setEditing(null)} className="p-2 rounded-lg hover:bg-gray-100"><X size={16} /></button>
      </div>
      <div className="flex flex-col gap-5">
        <Field label="Title"><input className={inputCls} value={editing.title||''} onChange={e=>setEditing(p=>({...p,title:e.target.value}))} placeholder="Post title..." /></Field>
        <Field label="Excerpt"><textarea className={textareaCls} rows={3} value={editing.excerpt||''} onChange={e=>setEditing(p=>({...p,excerpt:e.target.value}))} /></Field>
        <Field label="Content (HTML or markdown)"><textarea className={textareaCls} rows={10} value={editing.content||''} onChange={e=>setEditing(p=>({...p,content:e.target.value}))} /></Field>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Field label="Category">
            <select className={inputCls} value={editing.category||''} onChange={e=>setEditing(p=>({...p,category:e.target.value}))}>
              <option value="">Select...</option>
              {CATEGORIES.map(c=><option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Author"><input className={inputCls} value={editing.author||'Syenovix'} onChange={e=>setEditing(p=>({...p,author:e.target.value}))} /></Field>
          <Field label="Read Time (min)"><input type="number" className={inputCls} value={editing.read_time||5} onChange={e=>setEditing(p=>({...p,read_time:Number(e.target.value)}))} /></Field>
        </div>
        <label className="flex items-center gap-2 cursor-pointer p-3 rounded-xl" style={{background:'rgba(94,14,215,0.05)',border:'1px solid rgba(94,14,215,0.12)'}}>
          <input type="checkbox" checked={!!editing.published} onChange={e=>setEditing(p=>({...p,published:e.target.checked}))} className="accent-[#5E0ED7] w-4 h-4" />
          <div>
            <span className="text-sm font-semibold" style={{color:'#18181B'}}>Published — visible on website</span>
            <p className="text-xs" style={{color:'#6B6B72'}}>Uncheck to save as draft (hidden from visitors)</p>
          </div>
        </label>
        {err && <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-lg">{err}</p>}
        <div className="flex gap-3">
          <button onClick={save} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white" style={{background:ACCENT}}>
            <Save size={14}/> {saving?'Saving...':'Save Post'}
          </button>
          <button onClick={()=>setEditing(null)} className="px-5 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 hover:bg-gray-50">Cancel</button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold" style={{fontFamily:"'Plus Jakarta Sans', sans-serif"}}>Blog Posts</h2>
        <button onClick={()=>setEditing({published:true})} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white" style={{background:ACCENT}}><Plus size={14}/> New Post</button>
      </div>
      {posts.length===0
        ? <div className="text-center py-20 text-gray-400"><FileText size={40} className="mx-auto mb-4 opacity-30"/><p className="text-sm">No posts yet.</p></div>
        : <div className="flex flex-col gap-3">
            {posts.map(p=>(
              <div key={p.id} className="flex items-center justify-between p-4 rounded-xl border border-gray-100 bg-white hover:border-gray-200">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{background:p.published?'rgba(16,185,129,0.1)':'rgba(0,0,0,0.05)',color:p.published?'#059669':'#71717A'}}>
                      {p.published?'Published':'Draft'}
                    </span>
                    {p.category&&<span className="text-[10px] text-gray-400">{p.category}</span>}
                  </div>
                  <p className="text-sm font-semibold text-gray-900 truncate">{p.title}</p>
                </div>
                <div className="flex gap-2 ml-4 shrink-0">
                  <button onClick={()=>setEditing(p)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"><Edit3 size={14}/></button>
                  <button onClick={()=>del(p.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400"><Trash2 size={14}/></button>
                </div>
              </div>
            ))}
          </div>
      }
    </div>
  );
}

// ── Testimonials ─────────────────────────────────────
function TestimonialsPanel() {
  const [items,setItems]=[],load=()=>api.getTestimonials().then(setItems).catch(()=>setItems([]));
  // eslint-disable-next-line
  const [_,forceUpdate]=useState(0);
  const [data,setData]=useState([]);
  const [editing,setEditing]=useState(null);
  const [saving,setSaving]=useState(false);

  useEffect(()=>{api.adminGetAllTestimonials().then(setData).catch(()=>api.getTestimonials().then(setData).catch(()=>setData([])));},[]);
  const reload=()=>api.adminGetAllTestimonials().then(setData).catch(()=>api.getTestimonials().then(setData).catch(()=>{}));

  async function save(){
    setSaving(true);
    try{
      if(editing.id) await api.updateTestimonial(editing.id,editing);
      else await api.createTestimonial(editing);
      await reload(); setEditing(null);
    }finally{setSaving(false);}
  }
  async function del(id){
    if(!window.confirm('Delete?'))return;
    await api.deleteTestimonial(id); await reload();
  }

  if(editing!==null) return(
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold" style={{fontFamily:"'Plus Jakarta Sans', sans-serif"}}>{editing.id?'Edit':'New'} Testimonial</h2>
        <button onClick={()=>setEditing(null)} className="p-2 rounded-lg hover:bg-gray-100"><X size={16}/></button>
      </div>
      <div className="flex flex-col gap-5">
        <Field label="Quote"><textarea className={textareaCls} rows={4} value={editing.quote||''} onChange={e=>setEditing(p=>({...p,quote:e.target.value}))}/></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Name"><input className={inputCls} value={editing.name||''} onChange={e=>setEditing(p=>({...p,name:e.target.value}))}/></Field>
          <Field label="Role"><input className={inputCls} value={editing.role||''} onChange={e=>setEditing(p=>({...p,role:e.target.value}))} placeholder="CTO, Company"/></Field>
          <Field label="Company"><input className={inputCls} value={editing.company||''} onChange={e=>setEditing(p=>({...p,company:e.target.value}))}/></Field>
          <Field label="Initials"><input className={inputCls} value={editing.initials||''} maxLength={2} onChange={e=>setEditing(p=>({...p,initials:e.target.value.toUpperCase()}))}/></Field>
        </div>
        <div className="flex gap-3">
          <button onClick={save} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white" style={{background:ACCENT}}><Save size={14}/>{saving?'Saving...':'Save'}</button>
          <button onClick={()=>setEditing(null)} className="px-5 py-2.5 rounded-xl text-sm font-semibold border border-gray-200">Cancel</button>
        </div>
      </div>
    </div>
  );

  return(
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold" style={{fontFamily:"'Plus Jakarta Sans', sans-serif"}}>Testimonials</h2>
        <button onClick={()=>setEditing({})} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white" style={{background:ACCENT}}><Plus size={14}/>New</button>
      </div>
      {data.length===0
        ?<div className="text-center py-20 text-gray-400"><Star size={40} className="mx-auto mb-4 opacity-30"/><p className="text-sm">No testimonials yet.</p></div>
        :<div className="flex flex-col gap-3">
          {data.map(t=>(
            <div key={t.id} className="flex items-center justify-between p-4 rounded-xl border bg-white" style={{borderColor:t.status==='pending'?'rgba(245,158,11,0.3)':t.status==='rejected'?'rgba(220,38,38,0.15)':'rgba(24,24,27,0.07)'}}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <p className="text-sm font-semibold text-gray-900">{t.name} <span className="font-normal text-gray-400">— {t.role}</span></p>
                  <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{background:t.status==='approved'?'rgba(16,185,129,0.1)':t.status==='pending'?'rgba(245,158,11,0.1)':'rgba(220,38,38,0.1)',color:t.status==='approved'?'#059669':t.status==='pending'?'#d97706':'#dc2626'}}>
                    {t.status||'approved'}
                  </span>
                </div>
                <p className="text-xs text-gray-400 truncate">"{(t.quote||'').slice(0,80)}..."</p>
              </div>
              <div className="flex gap-1.5 ml-4 shrink-0">
                {t.status==='pending'&&<>
                  <button onClick={()=>{api.approveTestimonial(t.id,'approved').then(reload);}} className="p-1.5 rounded-lg hover:bg-green-50 text-green-600" title="Approve"><CheckCircle2 size={14}/></button>
                  <button onClick={()=>{api.approveTestimonial(t.id,'rejected').then(reload);}} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400" title="Reject"><XCircle size={14}/></button>
                </>}
                <button onClick={()=>setEditing(t)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"><Edit3 size={14}/></button>
                <button onClick={()=>del(t.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400"><Trash2 size={14}/></button>
              </div>
            </div>
          ))}
        </div>
      }
    </div>
  );
}

// ── Case Studies ─────────────────────────────────────
function CaseStudiesPanel() {
  const [data,setData]=useState([]);
  const [editing,setEditing]=useState(null);
  const [saving,setSaving]=useState(false);

  useEffect(()=>{api.getCaseStudies().then(setData).catch(()=>setData([]));},[]);
  const reload=()=>api.getCaseStudies().then(setData).catch(()=>{});

  function updateMetric(i,field,val){
    const m=[...(editing.metrics||[{},{},{}])];
    m[i]={...m[i],[field]:val};
    setEditing(p=>({...p,metrics:m}));
  }

  async function save(){
    setSaving(true);
    try{
      const d={...editing,metrics:(editing.metrics||[]).filter(m=>m.val&&m.label)};
      if(editing.id) await api.updateCaseStudy(editing.id,d);
      else await api.createCaseStudy(d);
      await reload(); setEditing(null);
    }finally{setSaving(false);}
  }
  async function del(id){
    if(!window.confirm('Delete?'))return;
    await api.deleteCaseStudy(id); await reload();
  }

  if(editing!==null) return(
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold" style={{fontFamily:"'Plus Jakarta Sans', sans-serif"}}>{editing.id?'Edit':'New'} Case Study</h2>
        <button onClick={()=>setEditing(null)} className="p-2 rounded-lg hover:bg-gray-100"><X size={16}/></button>
      </div>
      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Industry"><input className={inputCls} value={editing.industry||''} onChange={e=>setEditing(p=>({...p,industry:e.target.value}))}/></Field>
          <Field label="Tag"><input className={inputCls} value={editing.tag||''} onChange={e=>setEditing(p=>({...p,tag:e.target.value}))} placeholder="AI / ML"/></Field>
        </div>
        <Field label="Title"><input className={inputCls} value={editing.title||''} onChange={e=>setEditing(p=>({...p,title:e.target.value}))}/></Field>
        <Field label="Description"><textarea className={textareaCls} rows={4} value={editing.description||''} onChange={e=>setEditing(p=>({...p,description:e.target.value}))}/></Field>
        <div>
          <label className="block text-[11px] font-semibold tracking-[0.1em] uppercase mb-3" style={{color:'#71717A'}}>Metrics (up to 3)</label>
          {[0,1,2].map(i=>(
            <div key={i} className="flex gap-3 mb-2">
              <input className={inputCls} placeholder="Value" value={(editing.metrics||[])[i]?.val||''} onChange={e=>updateMetric(i,'val',e.target.value)} style={{flex:1}}/>
              <input className={inputCls} placeholder="Label" value={(editing.metrics||[])[i]?.label||''} onChange={e=>updateMetric(i,'label',e.target.value)} style={{flex:2}}/>
            </div>
          ))}
        </div>
        <div className="flex gap-3">
          <button onClick={save} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white" style={{background:ACCENT}}><Save size={14}/>{saving?'Saving...':'Save'}</button>
          <button onClick={()=>setEditing(null)} className="px-5 py-2.5 rounded-xl text-sm font-semibold border border-gray-200">Cancel</button>
        </div>
      </div>
    </div>
  );

  return(
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold" style={{fontFamily:"'Plus Jakarta Sans', sans-serif"}}>Case Studies</h2>
        <button onClick={()=>setEditing({})} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white" style={{background:ACCENT}}><Plus size={14}/>New</button>
      </div>
      {data.length===0
        ?<div className="text-center py-20 text-gray-400"><Briefcase size={40} className="mx-auto mb-4 opacity-30"/><p className="text-sm">No case studies yet.</p></div>
        :<div className="flex flex-col gap-3">
          {data.map(c=>(
            <div key={c.id} className="flex items-center justify-between p-4 rounded-xl border border-gray-100 bg-white">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[10px] text-gray-400">{c.industry}</span>
                  {c.tag&&<span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{background:'rgba(94,14,215,0.08)',color:ACCENT}}>{c.tag}</span>}
                </div>
                <p className="text-sm font-semibold text-gray-900 truncate">{c.title}</p>
              </div>
              <div className="flex gap-2 ml-4 shrink-0">
                <button onClick={()=>setEditing({...c,metrics:c.metrics||[]})} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"><Edit3 size={14}/></button>
                <button onClick={()=>del(c.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400"><Trash2 size={14}/></button>
              </div>
            </div>
          ))}
        </div>
      }
    </div>
  );
}

// ── Enquiries ─────────────────────────────────────────
function EnquiriesPanel() {
  const [items,setItems]=useState([]);
  const [selected,setSelected]=useState(null);
  const load=()=>api.getEnquiries().then(setItems).catch(()=>setItems([]));
  useEffect(()=>{load();},[]);

  async function markRead(id){
    await api.markEnquiryRead(id);
    setItems(p=>p.map(e=>e.id===id?{...e,read:true}:e));
    if(selected?.id===id) setSelected(p=>({...p,read:true}));
  }
  async function del(id){
    if(!window.confirm('Delete enquiry?'))return;
    await api.deleteEnquiry(id); await load(); if(selected?.id===id)setSelected(null);
  }

  const unread=items.filter(e=>!e.read).length;

  if(selected) return(
    <div>
      <button onClick={()=>setSelected(null)} className="flex items-center gap-1.5 text-sm font-semibold mb-6" style={{color:ACCENT}}>← Back to Enquiries</button>
      <div className="rounded-2xl border border-gray-100 bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg" style={{fontFamily:"'Plus Jakarta Sans',sans-serif"}}>{selected.name||'Anonymous'}</h3>
          <div className="flex gap-2">
            {!selected.read&&<button onClick={()=>markRead(selected.id)} className="text-xs px-3 py-1.5 rounded-lg font-medium" style={{background:'rgba(16,185,129,0.1)',color:'#059669'}}>Mark Read</button>}
            <button onClick={()=>del(selected.id)} className="text-xs px-3 py-1.5 rounded-lg font-medium" style={{background:'rgba(220,38,38,0.08)',color:'#dc2626'}}>Delete</button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-5 text-sm">
          <div><span className="text-gray-400">Email: </span><span className="font-medium text-gray-900">{selected.email}</span></div>
          {selected.company&&<div><span className="text-gray-400">Company: </span><span className="font-medium text-gray-900">{selected.company}</span></div>}
          {selected.service&&<div><span className="text-gray-400">Service: </span><span className="font-medium text-gray-900">{selected.service}</span></div>}
          <div><span className="text-gray-400">Date: </span><span className="font-medium text-gray-900">{new Date(selected.created_at).toLocaleDateString()}</span></div>
        </div>
        <div className="rounded-xl p-4" style={{background:'#F5F3F0'}}>
          <p className="text-sm leading-relaxed text-gray-700">{selected.message}</p>
        </div>
        <a href={`mailto:${selected.email}`} className="inline-flex items-center gap-2 mt-4 text-sm font-semibold rounded-xl px-4 py-2 text-white" style={{background:ACCENT}}>Reply via Email</a>
      </div>
    </div>
  );

  return(
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold" style={{fontFamily:"'Plus Jakarta Sans',sans-serif"}}>Enquiries</h2>
          {unread>0&&<p className="text-sm text-gray-500 mt-0.5">{unread} unread</p>}
        </div>
      </div>
      {items.length===0
        ?<div className="text-center py-20 text-gray-400"><Inbox size={40} className="mx-auto mb-4 opacity-30"/><p className="text-sm">No enquiries yet.</p></div>
        :<div className="flex flex-col gap-2">
          {items.map(e=>(
            <div key={e.id} onClick={()=>{setSelected(e);if(!e.read)markRead(e.id);}} className="flex items-start justify-between p-4 rounded-xl border bg-white cursor-pointer hover:border-gray-300 transition-colors" style={{borderColor:e.read?'rgba(24,24,27,0.07)':'rgba(94,14,215,0.2)',background:e.read?'#fff':'rgba(94,14,215,0.02)'}}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  {!e.read&&<span className="w-2 h-2 rounded-full shrink-0" style={{background:ACCENT}}/>}
                  <span className="text-sm font-semibold text-gray-900">{e.name||'Anonymous'}</span>
                  <span className="text-xs text-gray-400">{e.email}</span>
                </div>
                <p className="text-xs text-gray-400 truncate">{(e.message||'').slice(0,80)}...</p>
              </div>
              <div className="flex items-center gap-2 ml-4 shrink-0">
                <span className="text-[10px] text-gray-400">{new Date(e.created_at).toLocaleDateString()}</span>
                <button onClick={ev=>{ev.stopPropagation();del(e.id);}} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400"><Trash2 size={13}/></button>
              </div>
            </div>
          ))}
        </div>
      }
    </div>
  );
}

// ── About Stats ────────────────────────────────────────
function AboutStatsPanel() {
  const [stats,setStats]=useState({years:8,team:120,industries:15});
  const [saving,setSaving]=useState(false);
  const [saved,setSaved]=useState(false);
  useEffect(()=>{api.getConfig('about').then(d=>{if(d?.years)setStats(d);}).catch(()=>{});}, []);
  async function save(){
    setSaving(true);
    try{await api.updateConfig('about',{years:Number(stats.years),team:Number(stats.team),industries:Number(stats.industries)});setSaved(true);setTimeout(()=>setSaved(false),2000);}
    finally{setSaving(false);}
  }
  return(
    <div>
      <h2 className="text-xl font-bold mb-2" style={{fontFamily:"'Plus Jakarta Sans',sans-serif"}}>About Us Stats</h2>
      <p className="text-sm text-gray-500 mb-8">Numbers shown in the About section (animated counters).</p>
      <div className="grid grid-cols-3 gap-5 mb-8">
        {[['years','Years Operating'],['team','Team Members'],['industries','Industries']].map(([k,l])=>(
          <Field key={k} label={l}><input type="number" className={inputCls} value={stats[k]||''} onChange={e=>setStats(p=>({...p,[k]:e.target.value}))}/></Field>
        ))}
      </div>
      <button onClick={save} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white" style={{background:ACCENT}}>
        {saved?<><Check size={14}/>Saved!</>:saving?'Saving...':<><Save size={14}/>Save About Stats</>}
      </button>
    </div>
  );
}

// ── Clients ────────────────────────────────────────────
function ClientsPanel() {
  const [data,setData]=useState([]);
  const [editing,setEditing]=useState(null);
  const [saving,setSaving]=useState(false);
  const reload=()=>api.getClients().then(setData).catch(()=>setData([]));
  useEffect(()=>{reload();},[]);
  async function save(){
    setSaving(true);
    try{
      if(editing.id) await api.updateClient(editing.id,editing);
      else await api.createClient(editing);
      await reload();setEditing(null);
    }finally{setSaving(false);}
  }
  async function del(id){if(!window.confirm('Remove client?'))return;await api.deleteClient(id);await reload();}

  if(editing!==null) return(
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold" style={{fontFamily:"'Plus Jakarta Sans',sans-serif"}}>{editing.id?'Edit':'Add'} Client</h2>
        <button onClick={()=>setEditing(null)} className="p-2 rounded-lg hover:bg-gray-100"><X size={16}/></button>
      </div>
      <div className="flex flex-col gap-5">
        <Field label="Company Name *"><input className={inputCls} value={editing.name||''} onChange={e=>setEditing(p=>({...p,name:e.target.value}))} placeholder="Acme Inc."/></Field>
        <Field label="Website"><input className={inputCls} value={editing.website||''} onChange={e=>setEditing(p=>({...p,website:e.target.value}))} placeholder="https://acme.com"/></Field>
        <Field label="Logo URL (optional)"><input className={inputCls} value={editing.logo_url||''} onChange={e=>setEditing(p=>({...p,logo_url:e.target.value}))} placeholder="https://..."/></Field>
        <div className="flex gap-3">
          <button onClick={save} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white" style={{background:ACCENT}}><Save size={14}/>{saving?'Saving...':'Save'}</button>
          <button onClick={()=>setEditing(null)} className="px-5 py-2.5 rounded-xl text-sm font-semibold border border-gray-200">Cancel</button>
        </div>
      </div>
    </div>
  );

  return(
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold" style={{fontFamily:"'Plus Jakarta Sans',sans-serif"}}>Clients</h2>
        <button onClick={()=>setEditing({})} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white" style={{background:ACCENT}}><Plus size={14}/>Add Client</button>
      </div>
      <p className="text-sm text-gray-500 mb-2">Clients shown in the "Trusted By" marquee on the website.</p>
      <p className="text-xs mb-6 px-3 py-2 rounded-lg" style={{background:'rgba(16,185,129,0.08)',color:'#059669'}}>
        ✓ Once you add clients here, the static placeholder names are replaced with your actual client list on the website.
      </p>
      <p className="text-xs mb-4 px-3 py-2 rounded-lg" style={{background:'rgba(94,14,215,0.06)',color:ACCENT}}>
        Share this link so clients can submit their own testimonial: <strong>https://syenovix.web.app/testimonials/submit</strong>
      </p>
      {data.length===0
        ?<div className="text-center py-16 text-gray-400"><Building2 size={40} className="mx-auto mb-4 opacity-30"/><p className="text-sm">No clients added yet.</p></div>
        :<div className="flex flex-col gap-2">
          {data.map(c=>(
            <div key={c.id} className="flex items-center justify-between p-4 rounded-xl border border-gray-100 bg-white">
              <div>
                <p className="text-sm font-semibold text-gray-900">{c.name}</p>
                {c.website&&<p className="text-xs text-gray-400">{c.website}</p>}
              </div>
              <div className="flex gap-2">
                <button onClick={()=>setEditing(c)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"><Edit3 size={14}/></button>
                <button onClick={()=>del(c.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400"><Trash2 size={14}/></button>
              </div>
            </div>
          ))}
        </div>
      }
    </div>
  );
}

// ── Admin Users ────────────────────────────────────────
function AdminUsersPanel() {
  const [admins,setAdmins]=useState([]);
  const [adding,setAdding]=useState(false);
  const [form,setForm]=useState({email:'',password:''});
  const [saving,setSaving]=useState(false);
  const [err,setErr]=useState('');
  const {user}=useAuth();
  const reload=()=>api.getAdmins().then(setAdmins).catch(()=>setAdmins([]));
  useEffect(()=>{reload();},[]);

  async function add(){
    setSaving(true);setErr('');
    try{await api.createAdmin(form);await reload();setAdding(false);setForm({email:'',password:''});}
    catch(e){setErr(e.message);}
    finally{setSaving(false);}
  }
  async function remove(id,email){
    if(email===user?.email){setErr('Cannot remove your own account');return;}
    if(!window.confirm(`Remove admin ${email}?`))return;
    await api.deleteAdmin(id);await reload();
  }

  const [changingPw, setChangingPw] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [pwErr, setPwErr] = useState('');
  const [pwOk, setPwOk] = useState(false);

  async function submitChangePassword() {
    setPwErr('');
    if (pwForm.next.length < 8) { setPwErr('New password must be at least 8 characters.'); return; }
    if (pwForm.next !== pwForm.confirm) { setPwErr('Passwords do not match.'); return; }
    try {
      await api.changePassword(pwForm.current, pwForm.next);
      setPwOk(true); setChangingPw(false); setPwForm({ current: '', next: '', confirm: '' });
      setTimeout(() => setPwOk(false), 3000);
    } catch (e) { setPwErr(e.message); }
  }

  return(
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold" style={{fontFamily:"'Plus Jakarta Sans',sans-serif"}}>Admin Users</h2>
        <div className="flex gap-2">
          {pwOk && <span className="text-xs text-green-600 font-semibold">Password updated!</span>}
          <button onClick={()=>setChangingPw(p=>!p)} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold border border-gray-200 hover:bg-gray-50">
            <Eye size={13}/> Change My Password
          </button>
          <button onClick={()=>setAdding(true)} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white" style={{background:ACCENT}}><Plus size={14}/>Add Admin</button>
        </div>
      </div>

      {changingPw && (
        <div className="rounded-2xl border border-gray-100 bg-white p-6 mb-6">
          <h3 className="font-semibold mb-4">Change Your Password</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <Field label="Current Password">
              <div className="relative">
                <input type={showCurrent?'text':'password'} className={inputCls} style={{paddingRight:36}} value={pwForm.current} onChange={e=>setPwForm(p=>({...p,current:e.target.value}))} placeholder="Current password"/>
                <button type="button" onClick={()=>setShowCurrent(p=>!p)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400"><Eye size={14}/></button>
              </div>
            </Field>
            <Field label="New Password">
              <div className="relative">
                <input type={showNext?'text':'password'} className={inputCls} style={{paddingRight:36}} value={pwForm.next} onChange={e=>setPwForm(p=>({...p,next:e.target.value}))} placeholder="Min 8 characters"/>
                <button type="button" onClick={()=>setShowNext(p=>!p)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400">{showNext?<EyeOff size={14}/>:<Eye size={14}/>}</button>
              </div>
            </Field>
            <Field label="Confirm New">
              <input type="password" className={inputCls} value={pwForm.confirm} onChange={e=>setPwForm(p=>({...p,confirm:e.target.value}))} placeholder="Repeat new password"/>
            </Field>
          </div>
          {pwErr && <p className="text-xs text-red-500 mb-3">{pwErr}</p>}
          <div className="flex gap-3">
            <button onClick={submitChangePassword} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white" style={{background:ACCENT}}><Save size={14}/>Update Password</button>
            <button onClick={()=>{setChangingPw(false);setPwErr('');}} className="px-4 py-2 rounded-xl text-sm font-semibold border border-gray-200">Cancel</button>
          </div>
        </div>
      )}

      {adding&&(
        <div className="rounded-2xl border border-gray-100 bg-white p-6 mb-6">
          <h3 className="font-semibold mb-4">New Admin</h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <Field label="Email"><input type="email" className={inputCls} value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))} placeholder="admin@syenovix.com"/></Field>
            <Field label="Password"><input type="password" className={inputCls} value={form.password} onChange={e=>setForm(p=>({...p,password:e.target.value}))} placeholder="Strong password"/></Field>
          </div>
          {err&&<p className="text-xs text-red-500 mb-3">{err}</p>}
          <div className="flex gap-3">
            <button onClick={add} disabled={saving} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white" style={{background:ACCENT}}><Save size={14}/>{saving?'Adding...':'Add Admin'}</button>
            <button onClick={()=>{setAdding(false);setErr('');}} className="px-4 py-2 rounded-xl text-sm font-semibold border border-gray-200">Cancel</button>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2">
        {admins.map(a=>(
          <div key={a.id} className="flex items-center justify-between p-4 rounded-xl border border-gray-100 bg-white">
            <div>
              <p className="text-sm font-semibold text-gray-900">{a.email}</p>
              <p className="text-xs text-gray-400">Added {new Date(a.created_at).toLocaleDateString()}</p>
            </div>
            {a.email!==user?.email
              ?<button onClick={()=>remove(a.id,a.email)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400"><Trash2 size={14}/></button>
              :<span className="text-xs px-2 py-1 rounded-full" style={{background:'rgba(94,14,215,0.08)',color:ACCENT}}>You</span>
            }
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Generic List Editor (reusable for services, methodology, etc.) ──
function ListEditorPanel({ configKey, label, fields, defaultItem }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.getConfig(configKey).then(d => { if (Array.isArray(d)) setItems(d); }).catch(() => {});
  }, [configKey]);

  async function saveAll(list) {
    setSavingAll(true);
    try { await api.updateConfig(configKey, list); setSaved(true); setTimeout(() => setSaved(false), 2000); }
    finally { setSavingAll(false); }
  }

  function saveEdit() {
    const updated = editing._index >= 0
      ? items.map((it, i) => i === editing._index ? editing : it)
      : [...items, editing];
    setItems(updated);
    saveAll(updated);
    setEditing(null);
  }

  function del(i) {
    if (!window.confirm('Remove this item?')) return;
    const updated = items.filter((_, idx) => idx !== i);
    setItems(updated);
    saveAll(updated);
  }

  if (editing !== null) return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {editing._index >= 0 ? 'Edit' : 'Add'} {label}
        </h2>
        <button onClick={() => setEditing(null)} className="p-2 rounded-lg hover:bg-gray-100"><X size={16} /></button>
      </div>
      <div className="flex flex-col gap-5">
        {fields.map(f => (
          <Field key={f.key} label={f.label}>
            {f.type === 'textarea'
              ? <textarea className={textareaCls} rows={3} value={editing[f.key] || ''} onChange={e => setEditing(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.placeholder} />
              : <input className={inputCls} value={editing[f.key] || ''} onChange={e => setEditing(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.placeholder} />
            }
          </Field>
        ))}
        <div className="flex gap-3">
          <button onClick={saveEdit} disabled={savingAll} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white" style={{ background: ACCENT }}>
            <Save size={14} /> {savingAll ? 'Saving...' : 'Save'}
          </button>
          <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-xl text-sm font-semibold border border-gray-200">Cancel</button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{label}</h2>
        <div className="flex gap-2">
          {saved && <span className="text-xs text-green-600 font-semibold">Saved!</span>}
          <button onClick={() => setEditing({ ...defaultItem, _index: -1 })} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white" style={{ background: ACCENT }}>
            <Plus size={14} /> Add
          </button>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        {items.map((item, i) => (
          <div key={i} className="flex items-center justify-between p-4 rounded-xl border border-gray-100 bg-white">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{item[fields[0].key]}</p>
              {fields[1] && <p className="text-xs text-gray-400 truncate mt-0.5">{item[fields[1].key]?.slice(0, 80)}</p>}
            </div>
            <div className="flex gap-2 ml-4 shrink-0">
              <button onClick={() => setEditing({ ...item, _index: i })} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"><Edit3 size={14} /></button>
              <button onClick={() => del(i)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-gray-400 py-8 text-center">No items yet. Click Add to create one.</p>}
      </div>
    </div>
  );
}

// ── Hero Content ───────────────────────────────────────
function HeroPanel() {
  const [config, setConfig] = useState({ words: ['Fearless', 'Vision', 'Delivered'], eyebrow: 'Technology Consulting & AI Studio', tagline: 'We design, build, and scale digital products — powered by engineering, design, and AI.', cta: 'Work With Us' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => { api.getHero().then(d => { if (d?.words) setConfig(d); }).catch(() => {}); }, []);
  async function save() {
    setSaving(true);
    try { await api.updateConfig('hero', { ...config, words: typeof config.words === 'string' ? config.words.split(',').map(w => w.trim()) : config.words }); setSaved(true); setTimeout(() => setSaved(false), 2000); }
    finally { setSaving(false); }
  }
  return (
    <div>
      <h2 className="text-xl font-bold mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Hero Content</h2>
      <p className="text-sm text-gray-500 mb-8">Controls the main hero section text on the homepage.</p>
      <div className="flex flex-col gap-5 mb-8">
        <Field label="Heading Words (comma separated)">
          <input className={inputCls} value={Array.isArray(config.words) ? config.words.join(', ') : config.words} onChange={e => setConfig(p => ({ ...p, words: e.target.value }))} placeholder="Fearless, Vision, Delivered" />
          <p className="text-xs text-gray-400 mt-1">These are the large animated words on the hero. Middle word gets accent color.</p>
        </Field>
        <Field label="Eyebrow Badge Text"><input className={inputCls} value={config.eyebrow || ''} onChange={e => setConfig(p => ({ ...p, eyebrow: e.target.value }))} /></Field>
        <Field label="Tagline"><textarea className={textareaCls} rows={2} value={config.tagline || ''} onChange={e => setConfig(p => ({ ...p, tagline: e.target.value }))} /></Field>
        <Field label="CTA Button Text"><input className={inputCls} value={config.cta || ''} onChange={e => setConfig(p => ({ ...p, cta: e.target.value }))} /></Field>
      </div>
      <button onClick={save} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white" style={{ background: ACCENT }}>
        {saved ? <><Check size={14} /> Saved!</> : saving ? 'Saving...' : <><Save size={14} /> Save Hero</>}
      </button>
    </div>
  );
}

// ── Contact Info ─────────────────────────────────────
function ContactPanel() {
  const [info, setInfo]     = useState({ email: 'hello@syenovix.com', phone: '+1 (415) 800-9200', hq: 'San Francisco, CA' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);

  useEffect(() => { api.getConfig('contact').then(d => { if (d?.email) setInfo(d); }).catch(() => {}); }, []);

  async function save() {
    setSaving(true);
    try { await api.updateConfig('contact', info); setSaved(true); setTimeout(() => setSaved(false), 2000); }
    finally { setSaving(false); }
  }

  return (
    <div>
      <h2 className="text-xl font-bold mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Contact Info</h2>
      <p className="text-sm text-gray-500 mb-8">Displayed in the Contact section of the website.</p>
      <div className="flex flex-col gap-5 mb-8">
        <Field label="Email"><input className={inputCls} value={info.email||''} onChange={e=>setInfo(p=>({...p,email:e.target.value}))} /></Field>
        <Field label="Phone"><input className={inputCls} value={info.phone||''} onChange={e=>setInfo(p=>({...p,phone:e.target.value}))} /></Field>
        <Field label="HQ / Location"><input className={inputCls} value={info.hq||''} onChange={e=>setInfo(p=>({...p,hq:e.target.value}))} /></Field>
      </div>
      <button onClick={save} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white" style={{ background: ACCENT }}>
        {saved ? <><Check size={14} /> Saved!</> : saving ? 'Saving...' : <><Save size={14} /> Save Contact Info</>}
      </button>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────
export default function AdminDashboard() {
  const [active,setActive]=useState('stats');
  const {user,logout}=useAuth();
  const navigate=useNavigate();

  async function handleLogout(){ logout(); navigate('/admin'); }

  const ServicesPanel    = () => <ListEditorPanel configKey="services"       label="Services"        fields={[{key:'title',label:'Title',placeholder:'Service name'},{key:'description',label:'Description',type:'textarea',placeholder:'What this service covers...'},{key:'tags',label:'Tags (comma separated)',placeholder:'Web Apps, Mobile, Cloud'},{key:'color',label:'Color',placeholder:'blue / purple / violet / amber / green / cyan / red'}]} defaultItem={{title:'',description:'',tags:'',color:'blue'}} />;
  const MethodologyPanel = () => <ListEditorPanel configKey="methodology"    label="Process Steps"   fields={[{key:'num',label:'Step Number',placeholder:'01'},{key:'title',label:'Title',placeholder:'Step title'},{key:'desc',label:'Description',type:'textarea',placeholder:'What happens in this step...'}]} defaultItem={{num:'',title:'',desc:''}} />;
  const AIPanel          = () => <ListEditorPanel configKey="ai_capabilities" label="AI Capabilities" fields={[{key:'title',label:'Title',placeholder:'Capability title'},{key:'desc',label:'Description',type:'textarea',placeholder:'What this capability covers...'}]} defaultItem={{title:'',desc:''}} />;
  const IndustriesPanel  = () => <ListEditorPanel configKey="industries"     label="Industries"      fields={[{key:'name',label:'Industry Name',placeholder:'Financial Services'},{key:'subs',label:'Sub-sectors (comma separated)',placeholder:'FinTech, InsurTech'},{key:'color',label:'Color',placeholder:'blue / red / amber / cyan / green / emerald / violet'}]} defaultItem={{name:'',subs:'',color:'blue'}} />;
  const AboutValuesPanel = () => <ListEditorPanel configKey="about_values"   label="About Values"    fields={[{key:'title',label:'Title',placeholder:'Value title'},{key:'desc',label:'Description',type:'textarea',placeholder:'What this value means...'},{key:'color',label:'Color',placeholder:'purple / cyan / amber / green'}]} defaultItem={{title:'',desc:'',color:'purple'}} />;

  const panels={
    enquiries:EnquiriesPanel, blog:BlogPanel, testimonials:TestimonialsPanel,
    casestudies:CaseStudiesPanel, clients:ClientsPanel,
    stats:StatsPanel, hero:HeroPanel, services:ServicesPanel,
    methodology:MethodologyPanel, ai:AIPanel, industries:IndustriesPanel,
    about:AboutStatsPanel, aboutvalues:AboutValuesPanel,
    contact:ContactPanel, admins:AdminUsersPanel,
  };
  const ActivePanel=panels[active];

  return(
    <div className="min-h-screen flex" style={{background:'#FAFAF8',fontFamily:"'Inter',sans-serif"}}>
      {/* Sidebar */}
      <aside className="w-60 shrink-0 flex flex-col" style={{background:'#111110',minHeight:'100dvh'}}>
        <div className="flex items-center gap-2.5 px-4 py-4 border-b" style={{borderColor:'rgba(255,255,255,0.07)'}}>
          <img src="/logo.png" alt="Syenovix" className="w-8 h-8 rounded-lg object-cover"/>
          <div>
            <div className="text-sm font-bold text-white tracking-wider uppercase" style={{fontFamily:"'Plus Jakarta Sans',sans-serif"}}>Syenovix</div>
            <div className="text-[10px]" style={{color:'rgba(255,255,255,0.3)'}}>Admin Portal</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 flex flex-col gap-1">
          {NAV.map(({id,label,icon:Icon})=>(
            <button key={id} onClick={()=>setActive(id)} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-left transition-colors"
              style={{background:active===id?`${ACCENT}18`:'transparent',color:active===id?'#c4a0ff':'rgba(255,255,255,0.45)'}}>
              <Icon size={15}/>{label}
            </button>
          ))}
        </nav>
        <div className="px-4 py-4 border-t" style={{borderColor:'rgba(255,255,255,0.07)'}}>
          <p className="text-[11px] truncate mb-2" style={{color:'rgba(255,255,255,0.3)'}}>{user?.email}</p>
          <button onClick={handleLogout} className="flex items-center gap-2 text-sm text-red-400 hover:text-red-300">
            <LogOut size={13}/> Sign Out
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 px-8 py-8 overflow-auto">
        <div className="max-w-4xl">
          <AnimatePresence mode="wait">
            <motion.div key={active} initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-8}} transition={{duration:0.22,ease}}>
              <ActivePanel/>
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
